import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';

export default function ImprintPage() {
  const { t } = useTranslation();

  return (
    <>
      <Head
        title={t('seo.imprint.title')}
        metas={[
          { name: 'description', content: t('seo.imprint.description') },
          { name: 'keywords', content: t('seo.imprint.keywords') },
          { property: 'og:title', content: t('seo.imprint.title') },
          { property: 'og:description', content: t('seo.imprint.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/imprint' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary' },
          { name: 'twitter:title', content: t('seo.imprint.title') },
          { name: 'twitter:description', content: t('seo.imprint.description') },
          { name: 'last-modified', content: '2026-06-10' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/imprint' },
        ]}
        scripts={[
          {
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://estevaclinic.com/' },
                { '@type': 'ListItem', position: 2, name: t('seo.imprint.title'), item: 'https://estevaclinic.com/imprint' },
              ],
            }),
          },
        ]}
      />
      <main className="bg-background-50 min-h-screen">
        <Navbar />

        <section className="relative pt-24 md:pt-32 pb-8 md:pb-12 px-6 md:px-10 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20architectural%20texture%20with%20soft%20warm%20beige%20and%20cream%20tones%2C%20elegant%20marble%20surface%20with%20subtle%20veins%2C%20luxurious%20spa%20atmosphere%2C%20soft%20diffused%20lighting%2C%20clean%20and%20sophisticated%20aesthetic%2C%20high-end%20interior%20design%20texture%2C%20no%20text%2C%20abstract%20background&width=1600&height=500&seq=imprint-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 to-foreground-950/90"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">
              {t('imprint.title')}
            </h1>
          </div>
        </section>

        <section className="py-12 md:py-20 px-4 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-10 space-y-10 md:space-y-12">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  {t('imprint.responsible')}
                </h2>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-building-4-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.address_label')}</p>
                      <p className="text-sm text-foreground-800 font-body leading-relaxed">
                        Esteva Clinic<br />
                        Rruga Bylis<br />
                        Tiranë 1001<br />
                        {t('pub.country_albania')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.phone_label')}</p>
                      <a href="tel:+41765593993" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-body cursor-pointer">
                        +41 76 559 39 93
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.email_label')}</p>
                      <a href="mailto:info@esteva.ch" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-body cursor-pointer break-words">
                        info@esteva.ch
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-user-star-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.authorized_label')}</p>
                      <p className="text-sm text-foreground-800 font-body">
                        Dr. Nertila Basha
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-background-200 pt-10 md:pt-12">
                <h4 id="standort" className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                  <a href="#standort" className="no-underline text-foreground-900">{t('legal.location_title')}</a>
                </h4>
                <div className="rounded-xl overflow-hidden border border-background-200/70 h-[280px] sm:h-[320px] md:h-[400px]">
                  <iframe
                    src="https://maps.google.com/maps?q=Rruga%20Bylis,%20Tiran%C3%AB%201001,%20Albania&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`Esteva Clinic ${t('legal.location_title')}`}
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>

              <div className="border-t border-background-200 pt-10 md:pt-12">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                  {t('imprint.disclaimer_title')}
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  {t('imprint.disclaimer_text')}
                </p>
              </div>

              <div className="border-t border-background-200 pt-10 md:pt-12">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-4">
                  {t('imprint.copyright_title')}
                </h3>
                <p className="text-sm text-foreground-600 font-body leading-relaxed">
                  {t('imprint.copyright_text')}
                </p>
              </div>

              <div className="border-t border-background-200 pt-10 md:pt-12">
                <Link
                  to="/privacy"
                  className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-5 py-3 min-h-[44px] rounded-full bg-primary-50 text-primary-700 text-xs tracking-[0.1em] uppercase font-label font-semibold hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-shield-check-line"></i>
                  {t('imprint.data_protection_link')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}